import React, { useState, useMemo, useEffect, useRef } from "react";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetCalculuses } from "api/calculuses/service";
import { useGetChannels } from "api/channels/service";
import { formatDateForDisplay, formatDateTimeForDisplay } from "utils/dateUtils";
import { formatPriceWithDollar } from "utils/currency";
import classNames from "classnames";
import AccountsFilters from "./components/AccountsFilters";
import AccountsCards from "./components/AccountsCards";
import InventoryStatusTicks, {
  useFilterStatusCounts,
} from "views/inventory/components/InventoryStatusTicks";
import {
  readListState,
  saveListState,
  useClearListStateOnLeave,
  LIST_STATE_FLOWS,
} from "utils/listStateSessionStorage";
import {
  formatAccountListStatusLabel,
  getAccountStatusColor,
  isBankruptcyAccount,
} from "utils/accountStatusColors";
import { resolveInsuranceDisplayStatus } from "utils/insuranceStatus";

const getStatusOptions = (t) => [
  { value: "active", label: t("accountStatus.active"), color: getAccountStatusColor("active") },
  { value: "bankruptcy", label: t("accountStatus.bankruptcy"), color: "#7c3aed" },
  { value: "closed", label: t("accountStatus.closed"), color: getAccountStatusColor("closed") },
  { value: "deleted", label: t("accountStatus.deleted"), color: "#64748b" },
  { value: "inactive", label: t("accountStatus.inactive"), color: "#6c757d" },
  { value: "out_for_repo", label: t("accountStatus.outForRepo"), color: getAccountStatusColor("out_for_repo") },
  { value: "repossessed", label: t("accountStatus.repossessed"), color: getAccountStatusColor("repossessed") },
  { value: "skip", label: t("accountStatus.skip"), color: "#0ea5e9" },
  { value: "transferred", label: t("accountStatus.transferred"), color: "#14b8a6" },
  { value: "charge_off", label: t("accountStatus.chargeOff"), color: getAccountStatusColor("charge_off") },
];

const getAccountTypeOptions = (t) => [
  { value: "local_lender_finance", label: t('accounts.localLenderFinance') },
  { value: "in_house_finance", label: t('accounts.inHouseFinance') },
  { value: "cash", label: t('accounts.cash') },
  { value: "wholesale", label: t('accounts.wholesale') },
];

const ACCOUNTS_LIST_STATE_KEY = "dealertiq-accounts-list-state";

const Accounts = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storedStateRef = useRef(readListState(ACCOUNTS_LIST_STATE_KEY));
  useClearListStateOnLeave(ACCOUNTS_LIST_STATE_KEY, LIST_STATE_FLOWS.accounts);
  const [search, setSearch] = useState(() => storedStateRef.current?.search ?? "");
  const [status, setStatus] = useState([]);
  const [locations, setLocations] = useState([]);
  const [accountType, setAccountType] = useState(() => storedStateRef.current?.accountType ?? "");
  const [page, setPage] = useState(() => storedStateRef.current?.page ?? 1);
  const [pageSize, setPageSize] = useState(() => storedStateRef.current?.pageSize ?? 20);
  const { data: locationsData, isLoading: locationsLoading } = useGetChannels("sales_location_branch");
  const [debouncedSearch, setDebouncedSearch] = useState(() => storedStateRef.current?.search ?? "");

  const statusOptionsData = useMemo(() => getStatusOptions(t), [t]);
  const accountTypeOptionsData = useMemo(() => getAccountTypeOptions(t), [t]);
  const isTheme2 = shellThemeVariant === "2";
  const AccountsTable = isTheme2 ? ReactTable2 : ReactTable;

  const statusArray = useMemo(
    () => status.map((option) => option.value),
    [status]
  );
  const locationsArray = useMemo(
    () => locations.map((option) => option.value),
    [locations]
  );

  const statusInitializedRef = useRef(false);
  const locationsInitializedRef = useRef(false);
  const listStateHydratedRef = useRef(false);
  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    if (statusInitializedRef.current || statusOptionsData.length === 0) return;

    const stored = storedStateRef.current;
    if (stored?.statusValues?.length) {
      setStatus(statusOptionsData.filter((opt) => stored.statusValues.includes(opt.value)));
    } else {
      const defaults = ["active", "repossessed", "out_for_repo"];
      setStatus(statusOptionsData.filter((opt) => defaults.includes(opt.value)));
    }
    statusInitializedRef.current = true;
  }, [statusOptionsData]);

  useEffect(() => {
    if (locationsInitializedRef.current || locationsLoading) return;

    const stored = storedStateRef.current;
    if (stored?.locationValues?.length && Array.isArray(locationsData?.results)) {
      const locationOptions = locationsData.results.map((location) => ({
        value: String(location.id),
        label: location.name,
      }));
      setLocations(locationOptions.filter((opt) => stored.locationValues.includes(opt.value)));
    }
    locationsInitializedRef.current = true;
  }, [locationsData, locationsLoading]);

  useEffect(() => {
    if (statusInitializedRef.current && locationsInitializedRef.current) {
      setFiltersReady(true);
    }
  }, [status, locations, statusOptionsData, locationsLoading]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const { data: calculuses, isLoading } = useGetCalculuses(
    debouncedSearch,
    statusArray,
    page,
    pageSize,
    locationsArray,
    accountType,
    filtersReady
  );

  const getAccountStatusValue = React.useCallback((row) => {
    if (isBankruptcyAccount(row)) return "bankruptcy";
    return row?.act_status || row?.acct_status || "";
  }, []);

  const statusFilterCounts = useFilterStatusCounts({
    selectedStatuses: status,
    allStatuses: statusOptionsData,
    results: calculuses?.results || [],
    getStatusValue: getAccountStatusValue,
    enabled: filtersReady && statusOptionsData.length > 0,
  });

  useEffect(() => {
    if (!statusInitializedRef.current || !locationsInitializedRef.current) return;

    if (!listStateHydratedRef.current) {
      listStateHydratedRef.current = true;
      return;
    }

    saveListState(ACCOUNTS_LIST_STATE_KEY, {
      search,
      statusValues: status.map((option) => option.value),
      locationValues: locations.map((option) => option.value),
      accountType,
      page,
      pageSize,
    });
  }, [search, status, locations, accountType, page, pageSize]);

  const serverPageCount = calculuses?.pagination?.pageCount;
  useEffect(() => {
    if (serverPageCount == null || serverPageCount < 1) return;
    if (page > serverPageCount) {
      setPage(serverPageCount);
    }
  }, [serverPageCount, page]);

  const accountColumns = [
    {
      Header: t('accounts.borrowerName'),
      accessor: "borrower_name",
      width: 150,
      Cell: ({ value }) => (
        <div style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          whiteSpace: "normal"
        }}>
          {value}
        </div>
      )
    },
    {
      accessor: "information",
      Header: <div style={{ textAlign: 'center', lineHeight: '1.3' }} >
        {t('accounts.acct')}
        <br />
        {t('accounts.loan')}
        <br />
        {t('accounts.homePhone')}
        <br />
        {t('accounts.cellPhone')}
      </div>,
      Cell: ({ value }) => (
        <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2", textAlign: 'center' }}>
          <span>{value?.booking_id || ""}</span>
          <span>{value?.loan || ""}</span>
          <span>{value?.home_phone || ""}</span>
          <span>{value?.cell_phone || ""}</span>
        </div>
      )
    },
    {
      Header: <div style={{ textAlign: 'center' }}>{t('accounts.stkNumber')}</div>,
      accessor: "stk_number",
      Cell: ({ row }) => (
        <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2", textAlign: 'center' }}>
          <span>{row.original.stk_number}</span>
          <span style={{ fontSize: "0.85em", opacity: 0.7 }}>{row.original.vin}</span>
        </div>
      )
    },
    {
      Header: <div style={{ textAlign: 'center' }} >{t('accounts.acctStatus')}</div>,
      accessor: "acct_status",
      Cell: ({ row }) => {
        const value = row.original.acct_status;
        const flags = row.original.flags || [];
        const isBankruptcy = Boolean(row.original.is_bankruptcy);
        const displayValue = formatAccountListStatusLabel(value, { flags, isBankruptcy });
        const color = getAccountStatusColor(value, { flags, isBankruptcy });

        return (
          <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2", textAlign: 'center' }}>
            <span style={{
              color: color,
              fontWeight: 'bold'
            }}>
              {displayValue}
            </span>
          </div>
        );
      }
    },
    {
      Header: <div style={{ textAlign: 'center' }}>{t('accounts.insStatus')}</div>,
      accessor: "ins_status",

      Cell: ({ row }) => {
        const insurance = row.original.insurance;
        const status = resolveInsuranceDisplayStatus(insurance);

        if (status.kind === "none") {
          return (
            <div style={{ textAlign: "center" }}>
              <span>{t("accounts.noInsurance", "No Insurance")}</span>
            </div>
          );
        }

        const expireDate = insurance?.expire_date;
        const label = status.translationKey
          ? t(`accounts.${status.translationKey}`, status.fallbackLabel)
          : status.fallbackLabel;

        return (
          <div style={{ whiteSpace: "pre-line", textAlign: "center" }}>
            <div
              style={{
                color: status.color,
                textTransform: "capitalize",
                fontSize: "12px",
                fontWeight:
                  status.kind === "cancelled" ||
                  status.kind === "expired" ||
                  status.kind === "active" ||
                  status.kind === "renewed" ||
                  status.kind === "new"
                    ? "bold"
                    : "normal",
              }}
            >
              {label}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: status.color,
              }}
            >
              {expireDate
                ? new Date(expireDate).toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                : "-"}
            </div>
          </div>
        );
      }
    },
    {
      Header: <div style={{ textAlign: 'center' }}>{t('accounts.lastNote')}</div>,
      accessor: "last_note",
      Cell: ({ value }) => (
        <div style={{ textAlign: 'center' }}
        >

          <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{value}</span>
        </div>
      )
    },
    {
      Header: <div style={{ textAlign: 'center' }}>{t('accounts.promiseDate')}</div>,
      accessor: "promise_date",
      Cell: ({ value, row }) => {
        const status = row.original.promise_status;
        let backgroundColor = "#7a7a8c"; // default grey
        if (status?.toLowerCase() === "kept" || status?.toLowerCase() === "pending") backgroundColor = "#16c784";
        if (status?.toLowerCase() === "broken") backgroundColor = "#ff5c5c";
        if (status?.toLowerCase() === "open") backgroundColor = "#ffb020";

        if (!value) return <div style={{ textAlign: "center" }}>-</div>;

        return (
          <div style={{ textAlign: "center" }}>
            <span
              style={{
                backgroundColor,
                color: "#fff",
                padding: "4px 8px",
                fontSize: "12px",
                display: "inline-block",
                minWidth: "90px",
              }}
            >
              {value}
            </span>
          </div>
        );
      },
      width: 150,
    },
    {
      Header: <div style={{ textAlign: 'center' }}>{t('accounts.promiseAmount')}</div>,
      accessor: "promise_amount",
      Cell: ({ value, row }) => {
        const status = row.original.promise_status;
        let backgroundColor = "#7a7a8c"; // default grey
        if (status?.toLowerCase() === "kept" || status?.toLowerCase() === "pending") backgroundColor = "#16c784";
        if (status?.toLowerCase() === "broken") backgroundColor = "#ff5c5c";
        if (status?.toLowerCase() === "open") backgroundColor = "#ffb020";

        if (!value) return <div style={{ textAlign: "center" }}>-</div>;

        return (
          <div style={{ textAlign: "center" }}>
            <span
              style={{
                backgroundColor,
                color: "#fff",
                padding: "4px 8px",
                fontSize: "12px",
                display: "inline-block",
                minWidth: "90px",
              }}
            >
              {formatPriceWithDollar(value)}
            </span>
          </div>
        );
      },
      width: 120,
    },
    {
      Header: <div style={{ textAlign: 'center' }}>{t('accounts.promiseStatus')}</div>,
      accessor: "promise_status",
      Cell: ({ value }) => {
        let backgroundColor = "#7a7a8c"; // default grey
        let displayValue = value;

        if (value?.toLowerCase() === "kept" || value?.toLowerCase() === "pending") {
          backgroundColor = "#16c784";
          displayValue = "Kept";
        }
        if (value?.toLowerCase() === "open") {
          backgroundColor = "#ffb020";
          displayValue = "Open";
        }
        if (value?.toLowerCase() === "broken") backgroundColor = "#ff5c5c";

        if (!value) return <div style={{ textAlign: "center" }}>-</div>;

        return (
          <div style={{ textAlign: "center" }}>
            <span
              style={{
                backgroundColor,
                color: "#fff",
                padding: "4px 8px",
                fontSize: "12px",
                display: "inline-block",
                minWidth: "90px",
                textTransform: "capitalize",
              }}
            >
              {displayValue}
            </span>
          </div>
        );
      },
      width: 120,
    },
    {
      Header: <div style={{ textAlign: "center" }}>{t("accounts.daysPd", "Days PD")}</div>,
      accessor: "days_pd",
      Cell: ({ value }) => {
        const lateDay = Number(value) || 0;
        let rowBgColor = '#28a745';
        if (lateDay > 0) rowBgColor = '#f6ad55';
        if (lateDay > 15) rowBgColor = '#f6a04d';
        if (lateDay > 30) rowBgColor = '#d97706';
        if (lateDay > 60) rowBgColor = '#bf6d00';
        if (lateDay >= 90) rowBgColor = '#dc3545';
        if (lateDay >= 120) rowBgColor = '#dc3545';

        return (
          <div style={{ textAlign: "center" }}>
            <span
              style={{
                backgroundColor: rowBgColor,
                color: "#fff",
                padding: "4px 8px",
                fontSize: "12px",
                fontWeight: "bold",
                display: "inline-block",
                minWidth: "60px",
              }}
            >
              {value}
            </span>
          </div>
        );
      },
      width: 100,
    },
    {
      Header: <div style={{ textAlign: "center" }}>{t("accounts.recency")}</div>,
      accessor: "recency",
      Cell: ({ value }) => <div style={{ textAlign: "center" }}>{value}</div>,
    },
    {
      id: "balance_summary",
      Header: (
        <div style={{ textAlign: "right", lineHeight: 1.35, fontSize: "11px", fontWeight: 800 }}>
          <div>{t("accounts.totalBalance")}</div>
          <div>{t("accounts.curDueAmt")}</div>
          <div>{t("accounts.insLoanCpiCurPrinBal")}</div>
        </div>
      ),
      accessor: (row) => row.balance_summary?.total ?? 0,
      Cell: ({ row }) => {
        const b = row.original.balance_summary;
        const fmt = (v) => {
          if (v == null || v === "") return "$0.00";
          return formatPriceWithDollar(Number(v) || 0);
        };
        return (
          <div style={{ textAlign: "right",  fontSize: "12px" }}>
            <div>{fmt(b?.total)}</div>
            <div>{fmt(b?.curDueAmt)}</div>
            <div>{fmt(b?.insLoanCpiCurPrinBal)}</div>
          </div>
        );
      },
      width: 158,
      minWidth: 158,
      maxWidth: 200,
    },
  ];

  const accountsData = (calculuses?.results || []).map((item) => {
    // Use promise object directly instead of payment_schedule
    const nextPromise = item.promise;

    return {
      borrower_name: item.customer?.full_name || "",
      documentId: item.documentId || "",
      booking_id: item.booking_id,
      days_pd: item.days_pd,
      information: {
        booking_id: item.booking_id || "",
        loan: item.inventory?.stock_number || "",
        home_phone: item.customer?.home_phone || "",
        cell_phone: item.customer?.cell_phone || "",
      },
      stk_number: item.inventory?.stock_number || "",
      vin: item.inventory?.vin || "",
      applicant_details: {
        name: item?.full_name || "",
        worksheets: item?.worksheets || [],
      },
      acct_status: item.act_status || "",
      flags: item.flags || [],
      is_bankruptcy: Boolean(item.is_bankruptcy),
      ins_status: item.ins_status || "",
      insurance: item.insurance || null,
      last_note: formatDateTimeForDisplay(item.last_note_date) || "",
      promise_date: nextPromise?.date
        ? formatDateForDisplay(nextPromise.date)
        : "",
      promise_amount: nextPromise?.amount || "",
      promise_status: nextPromise?.promise_status || "",
      recency: item?.recency,
      balance_summary: {
        total: item.total_balance,
        curDueAmt: item.next_payment_amount ?? item.next_payment_amount ?? 0,
        insLoanCpiCurPrinBal: item.cpi_loan_balance ?? item.cpi_loan_balance ?? 0,
      },
    };
  });

  const filters = (
    <AccountsFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      search={search}
      onSearchChange={setSearch}
      status={status}
      onStatusChange={setStatus}
      accountType={accountType}
      onAccountTypeChange={setAccountType}
      locations={locations}
      onLocationsChange={setLocations}
      statusOptions={statusOptionsData}
      accountTypeOptions={accountTypeOptionsData}
      locationsData={locationsData}
    />
  );

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: isTheme2 ? "13px" : "12px", minWidth: 0, maxWidth: "100%" }}
    >
      <AccountsTable
        activeColor={activeColor}
        data={accountsData}
        columns={accountColumns}
        className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
        manualPagination={true}
        pageCount={calculuses?.pagination?.pageCount || 1}
        pageIndex={page - 1}
        totalItems={calculuses?.pagination?.total}
        pageSize={pageSize}
        noDataText={t("accounts.noRecordsFound")}
        loading={isLoading}
        loadingText={t("accounts.loading")}
        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        paginationMetaExtra={<InventoryStatusTicks items={statusFilterCounts} />}
        onRowClick={(rowData) => {
          if (rowData.booking_id) {
            navigate(`/accounts/${rowData.booking_id}`);
          }
        }}
      />
    </div>
  );


  return (
    <div
      className={classNames("content", "search-accounts", {
        "search-accounts--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-accounts-inset": isTheme2,
      })}
    >
      <AccountsCards
        isTheme2={isTheme2}
        filters={filters}
        table={table}
      />
    </div>
  );
};

export default Accounts;
